// src/api/sku/skuApi.ts
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse as CommonApiResponse } from "src/api/common/apiTypes";
import type {
  ApiResponse,
  SkuItem,
  SkuListParams,
  SkuListResponse,
  SkuCreatePayload,
  SkuUpdatePayload,
} from "src/types/sku";

export interface SkuInventoryItem {
  id: number;
  skuCode: string;
  barcode: string;
  skuName: string;
  recentSalesQuantity: number;
  availableQuantity: number;
  safetyStock?: number | null;
  stockStatus: string;
  inventoryId: null | number;
  inventoryType: "MANUAL" | "WMS" | "FASSTO";
  warehouseName: string;
  linkedProductCount: number;
  expiryDate: string | null;
  expiryManaged: boolean;
}

export interface SkuInventoryListParams {
  skuName?: string;
  warehouseId?: number;
  isActive?: boolean;
  page: number;
  size: number;
  order: string;
}

export interface makeInventoryParams {
  skuId: number;
  warehouseId: number;
  totalQuantity: number;
  availableQuantity: number;
  safetyStock: number;
}

export interface SkuInventoryListResponse {
  statusCode: number;
  message: string;
  data: {
    content: SkuInventoryItem[];
    page: {
      number: number;
      size: number;
      totalElements: number;
      totalPages: number;
    };
  };
}

export interface SkuMappingItem {
  mappingId: number;
  productId: number;
  productCode: string;
  productName: string;
  variantId: number;
  variantName: string;
  barcode: string;
  skuCode: string;
  skuName: string;
  quantityPerOrder: number;
}

export interface SkuMappingListParams {
  productId?: number;
  skuId?: number;
  warehouseId?: number;
  page: number;
  size: number;
  order: string;
}

export interface SkuAdjustInventoryParam {
  transactionType: "ADJUST_PLUS" | "ADJUST_MINUS";
  quantity: number;
  reason: string;
}

export interface SkuMappingListResponse {
  statusCode: number;
  message: string;
  data: {
    content: SkuMappingItem[];
    page: {
      number: number;
      size: number;
      totalElements: number;
      totalPages: number;
    };
  };
}

export interface SkuConnectFasstoResponse {
  statusCode: number;
  message: string;
  data: object;
}

export async function getSkuListApi(params: SkuListParams): Promise<SkuListResponse> {
  const res = await axiosInstance.get<SkuListResponse>("/v1/admin/baseinfo/sku/list", { params });
  return res.data;
}

export async function createSkuApi(payload: SkuCreatePayload): Promise<ApiResponse<SkuItem>> {
  const res = await axiosInstance.post<ApiResponse<SkuItem>>("/v1/admin/baseinfo/sku", payload);
  return res.data;
}

export async function updateSkuApi(
  id: number,
  payload: SkuUpdatePayload
): Promise<ApiResponse<SkuItem>> {
  const res = await axiosInstance.put<ApiResponse<SkuItem>>(
    `/v1/admin/baseinfo/sku/${id}`,
    payload
  );
  return res.data;
}

export async function deleteSkuApi(id: number): Promise<ApiResponse<null>> {
  const res = await axiosInstance.delete<ApiResponse<null>>(`/v1/admin/baseinfo/sku/${id}`);
  return res.data;
}

export async function getSkuInventoryListApi(
  params: SkuInventoryListParams
): Promise<SkuInventoryListResponse> {
  const res = await axiosInstance.get<SkuInventoryListResponse>(
    "/v1/admin/baseinfo/sku/inventory/list",
    {
      params: {
        skuName: params.skuName || undefined,
        warehouseId: params.warehouseId || undefined,
        isActive: params.isActive,
        page: params.page,
        size: params.size,
        order: params.order,
      },
    }
  );
  return res.data;
}

export async function getSkuMappingListApi(
  params: SkuMappingListParams
): Promise<SkuMappingListResponse> {
  const res = await axiosInstance.get<SkuMappingListResponse>(
    "/v1/admin/baseinfo/sku-mapping/list",
    {
      params: {
        productId: params.productId || undefined,
        skuId: params.skuId || undefined,
        warehouseId: params.warehouseId || undefined,
        page: params.page,
        size: params.size,
        order: params.order,
      },
    }
  );
  return res.data;
}

export async function connectSkuToFasstoApi(skuId: number): Promise<SkuConnectFasstoResponse> {
  const res = await axiosInstance.post<SkuConnectFasstoResponse>(`/v1/admin/fassto/goods/${skuId}`);
  return res.data;
}

export async function adjustInventoryApi(
  id: number,
  param: SkuAdjustInventoryParam
): Promise<ApiResponse<null>> {
  const res = await axiosInstance.post<ApiResponse<null>>(
    `/v1/admin/baseinfo/inventory/${id}/adjust`,
    param
  );
  return res.data;
}

// needsInventory 인 경우 사용될 재고생성 api
export async function makeInventoryApi(param: makeInventoryParams): Promise<ApiResponse<null>> {
  const res = await axiosInstance.post<ApiResponse<null>>(`/v1/admin/baseinfo/inventory`, param);
  return res.data;
}

export interface InventoryDetail {
  inventoryId: number;
  skuId: number;
  skuCode: string;
  skuName: string;
  barcode: string;
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  inventoryType: "MANUAL" | "WMS" | "FASSTO";
  totalQuantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  badQuantity: number;
  safetyStock: number;
  lastSyncedAt: string | null;
  syncStatus: string;
  syncMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryUpdatePayload {
  totalQuantity: number;
  availableQuantity: number;
  safetyStock: number;
  reason: string;
}

export async function getInventoryApi(id: number): Promise<ApiResponse<InventoryDetail>> {
  const res = await axiosInstance.get<ApiResponse<InventoryDetail>>(
    `/v1/admin/baseinfo/inventory/${id}`
  );
  return res.data;
}

export async function updateInventoryApi(
  id: number,
  payload: InventoryUpdatePayload
): Promise<ApiResponse<InventoryDetail>> {
  const res = await axiosInstance.put<ApiResponse<InventoryDetail>>(
    `/v1/admin/baseinfo/inventory/${id}`,
    payload
  );
  return res.data;
}

export interface CreateSkuMappingPayload {
  skuId: number;
  productId: number;
  variantId?: number | null;
  quantityPerOrder: number;
}

export interface UpdateSkuMappingPayload {
  quantityPerOrder: number;
}

export async function createSkuMappingApi(
  payload: CreateSkuMappingPayload
): Promise<CommonApiResponse<null>> {
  const res = await axiosInstance.post<CommonApiResponse<null>>(
    "/v1/admin/baseinfo/sku-mapping",
    payload
  );
  return res.data;
}

export async function updateSkuMappingApi(
  mappingId: number,
  payload: UpdateSkuMappingPayload
): Promise<CommonApiResponse<null>> {
  const res = await axiosInstance.put<CommonApiResponse<null>>(
    `/v1/admin/baseinfo/sku-mapping/${mappingId}`,
    payload
  );
  return res.data;
}

export async function deleteSkuMappingApi(mappingId: number): Promise<CommonApiResponse<null>> {
  const res = await axiosInstance.delete<CommonApiResponse<null>>(
    `/v1/admin/baseinfo/sku-mapping/${mappingId}`
  );
  return res.data;
}

export interface FasstoStockSyncResponse {
  statusCode: number;
  message: string;
  data: number;
}

export async function syncFasstoStockApi(): Promise<FasstoStockSyncResponse> {
  const res = await axiosInstance.post<FasstoStockSyncResponse>("/v1/admin/fassto/stock/sync");
  return res.data;
}

// ── 재고 일괄 갱신 ──────────────────────────────────────────────────────────

export interface ExcelExportAccepted {
  jobId: string;
  status: "PENDING" | "PROCESSING" | "DONE" | "FAILED";
  expiresAt: string;
}

export interface ExcelExportStatusResponse {
  jobId: string;
  type: string;
  status: "PENDING" | "PROCESSING" | "DONE" | "FAILED";
  downloadUrl?: string;
  errorMessage?: string;
  expiresAt: string;
}

export interface InventoryBulkUpdateFailure {
  rowIndex: number;
  inventoryId: number;
  skuCode: string;
  reason: string;
}

export interface InventoryBulkUpdateResponse {
  attempted: number;
  succeeded: number;
  failed: number;
  failures: InventoryBulkUpdateFailure[];
  batchId: string;
}

/** 재고 일괄 갱신 양식 생성 요청 (비동기) — jobId 반환 */
export async function requestInventoryBulkUpdateTemplateApi(params: {
  skuName?: string;
  warehouseId?: number;
  isActive?: boolean;
}): Promise<{ statusCode: number; message: string; data: ExcelExportAccepted }> {
  const res = await axiosInstance.post<{
    statusCode: number;
    message: string;
    data: ExcelExportAccepted;
  }>("/v1/admin/baseinfo/sku/inventory/bulk-update/template-jobs", null, {
    params: {
      skuName: params.skuName || undefined,
      warehouseId: params.warehouseId || undefined,
      isActive: params.isActive,
    },
  });
  return res.data;
}

/** 엑셀 내보내기 상태 폴링 */
export async function getExcelExportStatusApi(
  jobId: string
): Promise<{ statusCode: number; message: string; data: ExcelExportStatusResponse }> {
  const res = await axiosInstance.get<{
    statusCode: number;
    message: string;
    data: ExcelExportStatusResponse;
  }>(`/v1/admin/excel/export/${jobId}`, { snackbar: { success: false } } as any);
  return res.data;
}

/** 재고 일괄 갱신 엑셀 업로드 */
export async function bulkUpdateInventoryByExcelApi(
  file: File
): Promise<{ statusCode: number; message: string; data: InventoryBulkUpdateResponse }> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axiosInstance.post<{
    statusCode: number;
    message: string;
    data: InventoryBulkUpdateResponse;
  }>("/v1/admin/baseinfo/sku/inventory/bulk-update/excel", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}
